import { useState, useEffect } from 'react';
import { Info, Lock } from 'lucide-react';
import { useConfigTab, FORM_STYLES, ConfigTimestamps, LoadingState } from './shared';

const DAY_ABBR = {
  MONDAY: 'MON', TUESDAY: 'TUE', WEDNESDAY: 'WED', THURSDAY: 'THU',
  FRIDAY: 'FRI', SATURDAY: 'SAT', SUNDAY: 'SUN',
};

export function CodRemittanceTab({ merchantId, reloadKey }) {
  const { loading, isNew, configValue, metadata } = useConfigTab(
    merchantId, 'MERCHANT_COD_REMITTANCE_REPORT_CONFIGURATIONS', reloadKey,
  );

  const [cycleMode, setCycleMode] = useState('INTERVAL_DAYS');
  const [cycleDays, setCycleDays] = useState('');
  const [daysOfWeek, setDaysOfWeek] = useState([]);
  const [reversalWindowDays, setReversalWindowDays] = useState('');

  useEffect(() => {
    if (!configValue) {
      setCycleMode('INTERVAL_DAYS');
      setCycleDays('');
      setDaysOfWeek([]);
      setReversalWindowDays('');
      return;
    }
    setCycleMode(configValue.cycle_mode || 'INTERVAL_DAYS');
    setCycleDays(configValue.cycle_days != null ? String(configValue.cycle_days) : '');
    setDaysOfWeek(Array.isArray(configValue.days_of_week) ? configValue.days_of_week : []);
    setReversalWindowDays(configValue.reversal_window_days != null ? String(configValue.reversal_window_days) : '');
  }, [configValue]);

  if (loading) return <LoadingState />;

  if (isNew) {
    return (
      <div className="flex items-center gap-2 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs sm:text-sm text-slate-600">
        <Lock className="w-4 h-4 shrink-0 text-slate-400" />
        COD Remittance configuration is managed by the admin team. Please contact support for setup.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs sm:text-sm text-amber-800">
        <Info className="w-3.5 h-3.5 shrink-0" />
        This configuration is managed by the admin team. Contact support for changes.
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-4">
        <div className={FORM_STYLES.sectionCard}>
          <h3 className={FORM_STYLES.sectionTitle}>
            <Lock className="w-3.5 h-3.5 text-slate-400" />
            Remittance Cycle Mode
          </h3>
          <div className="flex flex-wrap gap-2 mb-3">
            {['INTERVAL_DAYS', 'WEEKLY_DAYS'].map((mode) => (
              <div
                key={mode}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                  cycleMode === mode
                    ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500/30'
                    : 'border-slate-200 bg-slate-50'
                }`}
              >
                <input type="radio" name="cycle_mode" value={mode} checked={cycleMode === mode} disabled className="accent-blue-600" />
                <span className="text-xs sm:text-sm font-medium text-slate-700">
                  {mode === 'INTERVAL_DAYS' ? 'Interval Days' : 'Weekly Days'}
                </span>
              </div>
            ))}
          </div>

          {cycleMode === 'INTERVAL_DAYS' && (
            <div className="max-w-[140px]">
              <label className={FORM_STYLES.label}>Cycle Days</label>
              <input type="text" value={cycleDays} readOnly disabled className={FORM_STYLES.inputDisabled} />
            </div>
          )}

          {cycleMode === 'WEEKLY_DAYS' && (
            <div>
              <label className={FORM_STYLES.label}>Days of Week</label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {Object.entries(DAY_ABBR).map(([day, abbr]) => (
                  <span
                    key={day}
                    className={`px-2.5 py-1.5 rounded-full text-xs font-medium border ${
                      daysOfWeek.includes(day)
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-slate-50 text-slate-400 border-slate-200'
                    }`}
                  >
                    {abbr}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={FORM_STYLES.sectionCard}>
          <h3 className={FORM_STYLES.sectionTitle}>
            <Lock className="w-3.5 h-3.5 text-slate-400" />
            Reversal Window
          </h3>
          <div>
            <label className={FORM_STYLES.label}>Days</label>
            <input type="text" value={reversalWindowDays || '-'} readOnly disabled className={FORM_STYLES.inputDisabled} />
          </div>
        </div>
      </div>

      <ConfigTimestamps metadata={metadata} />
    </div>
  );
}
